import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { markNotificationRead } from '../notificationRead';
import { Megaphone, Bell, Trophy, X } from 'lucide-react';

/* ===================== ANNOUNCEMENTS ===================== */
export function AnnouncementsPanel({ close }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/announcements');
        setItems(res.data?.announcements || res.data?.items || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="h-full flex flex-col" data-testid="announcements-panel">
      <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(255,217,61,0.25)' }}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #FFD93D, #F4B400)', boxShadow: '0 4px 14px rgba(244,180,0,0.45)' }}>
          <Megaphone className="w-5 h-5" style={{ color: '#3F2A00' }} strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="display font-extrabold text-lg" style={{ color: '#fff' }}>Announcements</h2>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>News & updates from the team</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && <div className="py-16 text-center text-white/60">Loading...</div>}
        {!loading && items.length === 0 && (
          <div className="py-16 text-center">
            <Megaphone className="w-10 h-10 mx-auto mb-3 text-amber-300 opacity-60" />
            <p className="display font-bold text-white/80">No announcements yet</p>
            <p className="text-sm text-white/50 mt-1">Check back later for news and promotions.</p>
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="rounded-xl p-4"
            style={{ background: 'rgba(255,217,61,0.08)', border: '1px solid rgba(255,217,61,0.35)' }}
            data-testid={`announcement-item-${a.id}`}>
            <p className="display font-extrabold text-sm" style={{ color: '#FFD93D' }}>{a.title || 'Update'}</p>
            {a.body && <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>{a.body}</p>}
            {a.created_at && (
              <p className="text-[10px] mt-2 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {new Date(a.created_at).toLocaleString()}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===================== NOTIFICATIONS ===================== */
export function NotificationsPanel({ close, onRead, detailOpen = false, detailItem = null, onOpenDetail, onCloseDetail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get('/api/notifications');
      setItems(res.data?.notifications || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const isUnread = (item) => !item?.read && !item?.is_read;

  const markOneRead = async (notificationId) => {
    try {
      if (!(await markNotificationRead(api, notificationId))) throw new Error('Notification was not marked read');
      onRead && onRead();
      setItems(prev => prev.map(item => (
        item.id === notificationId
          ? { ...item, read: true, is_read: true }
          : item
      )));
      return true;
    } catch {
      toast.error('Failed');
      return false;
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      onRead && onRead();
      toast.dismiss();
      load();
    } catch { toast.error('Failed'); }
  };

  const selectedCreatedAt = detailItem?.created_at ? new Date(detailItem.created_at).toLocaleString() : '';
  const selectedKind = detailItem?.category || detailItem?.type || detailItem?.kind || 'Alert';

  return (
    <div className="relative h-full flex flex-col" data-testid="notifications-panel">
      <div className={`h-full flex flex-col transition duration-200 ${detailOpen ? 'blur-sm pointer-events-none select-none' : ''}`}>
        <div className="px-5 pr-20 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(0,77,97,0.25)' }}>
          <span className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(180deg, var(--vl-btn-primary-from, #004D61), var(--vl-btn-primary-to, #004D61))', boxShadow: '0 4px 14px rgba(0,77,97,0.45)' }}>
            <Bell className="w-5 h-5" style={{ color: 'var(--vl-btn-primary-text, #031A2A)' }} strokeWidth={2.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="display font-extrabold text-lg" style={{ color: '#fff' }}>Alerts</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Deposits, cashouts, wallet updates, and activity</p>
          </div>
          {items.some(isUnread) && (
            <button onClick={markAllRead} className="text-xs display font-bold px-3 py-1.5 rounded-lg"
              style={{ background: 'rgba(0,77,97,0.15)', color: 'var(--vl-neon, #004D61)', border: '1px solid rgba(0,77,97,0.35)' }}
              data-testid="notif-mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && <div className="py-16 text-center text-white/60">Loading...</div>}
          {!loading && items.length === 0 && (
            <div className="py-16 text-center">
              <Bell className="w-10 h-10 mx-auto mb-3 text-emerald-300 opacity-60" />
              <p className="display font-bold text-white/80">You're all caught up</p>
              <p className="text-sm text-white/50 mt-1">New alerts will appear here.</p>
            </div>
          )}
          {items.map((n) => {
            const unread = isUnread(n);
            return (
              <button
                key={n.id}
                type="button"
                onClick={async () => {
                  let nextItem = n;
                  if (unread) {
                    const markedRead = await markOneRead(n.id);
                    if (!markedRead) return;
                    nextItem = { ...n, read: true, is_read: true };
                  }
                  toast.dismiss();
                  onOpenDetail && onOpenDetail(nextItem);
                }}
                className="w-full rounded-lg px-3 py-2.5 text-left transition active:scale-[0.995]"
                style={{
                  background: unread ? 'rgba(0,77,97,0.08)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${unread ? 'rgba(0,77,97,0.35)' : 'rgba(255,255,255,0.06)'}`,
                }}
                data-testid={`notif-item-${n.id}`}>
                <div className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full shrink-0"
                    style={{ background: unread ? 'var(--vl-neon, #004D61)' : 'transparent', boxShadow: unread ? '0 0 8px var(--vl-neon, #004D61)' : 'none' }} />
                  <div className="flex-1 min-w-0">
                    <p className="display text-sm font-bold" style={{ color: '#fff' }}>{n.title || 'Notification'}</p>
                    {n.message && <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.72)' }}>{n.message}</p>}
                    {n.created_at && (
                      <p className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {detailOpen && detailItem && (
        <div className="absolute inset-0 z-30 flex items-center justify-center px-4 py-6" data-testid="notif-detail-overlay">
          <button
            type="button"
            className="absolute inset-0 bg-black/55 backdrop-blur-md"
            aria-label="Back to alerts"
            onClick={onCloseDetail}
          />
          <div
            className="relative z-10 w-full max-w-sm rounded-3xl p-5 shadow-2xl"
            style={{
              background: 'linear-gradient(180deg, rgba(16,28,39,0.98), rgba(6,13,18,0.98))',
              border: '1px solid rgba(0,77,97,0.35)',
              boxShadow: '0 18px 60px rgba(0,0,0,0.55), 0 0 34px rgba(0,77,97,0.18)',
            }}
          >
            <button
              type="button"
              onClick={onCloseDetail}
              className="absolute right-3 top-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff' }}
              aria-label="Close alert detail"
            >
              <X className="w-5 h-5" />
            </button>
            <span
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{
                background: 'linear-gradient(180deg, var(--vl-btn-primary-from, #004D61), var(--vl-btn-primary-to, #004D61))',
                boxShadow: '0 10px 28px rgba(0,77,97,0.35)',
              }}
            >
              <Bell className="w-6 h-6" style={{ color: 'var(--vl-btn-primary-text, #031A2A)' }} strokeWidth={2.5} />
            </span>
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: 'var(--vl-neon, #004D61)' }}>
              {String(selectedKind).replace(/[_-]/g, ' ')}
            </p>
            <h3 className="display text-xl font-extrabold pr-8" style={{ color: '#fff' }}>
              {detailItem.title || 'Notification'}
            </h3>
            {detailItem.message && (
              <p className="text-sm leading-relaxed mt-3" style={{ color: 'rgba(255,255,255,0.78)' }}>
                {detailItem.message}
              </p>
            )}
            {selectedCreatedAt && (
              <p className="text-[11px] mt-5 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.48)' }}>
                {selectedCreatedAt}
              </p>
            )}
            <button
              type="button"
              onClick={onCloseDetail}
              className="mt-5 w-full rounded-xl py-3 display font-extrabold"
              style={{
                background: 'linear-gradient(180deg, var(--vl-btn-primary-from, #004D61), var(--vl-btn-primary-to, #004D61))',
                color: 'var(--vl-btn-primary-text, #031A2A)',
                boxShadow: '0 10px 26px rgba(0,77,97,0.25)',
              }}
            >
              Back to alerts
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================== ACHIEVEMENTS ===================== */
export function AchievementsPanel({ close }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/achievements');
        setItems(res.data?.achievements || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="h-full flex flex-col" data-testid="achievements-panel">
      <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(249,115,22,0.25)' }}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #F97316, #C2410C)', boxShadow: '0 4px 14px rgba(249,115,22,0.45)' }}>
          <Trophy className="w-5 h-5" style={{ color: '#fff' }} strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="display font-extrabold text-lg" style={{ color: '#fff' }}>Achievements</h2>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Unlock rewards by playing</p>
        </div>
        <button onClick={close} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5" aria-label="Close">
          <X className="w-4 h-4" style={{ color: '#fff' }} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading && <div className="py-16 text-center text-white/60">Loading...</div>}
        {!loading && items.length === 0 && (
          <div className="py-16 text-center">
            <Trophy className="w-10 h-10 mx-auto mb-3 text-orange-300 opacity-60" />
            <p className="display font-bold text-white/80">No achievements yet</p>
            <p className="text-sm text-white/50 mt-1">Play games to start earning badges and rewards.</p>
          </div>
        )}
        {items.map((a) => {
          const progress = Math.min(100, Math.round(((a.progress || 0) / Math.max(1, a.target || 1)) * 100));
          const unlocked = a.unlocked || a.claimed || progress >= 100;
          return (
            <div key={a.id} className="rounded-xl p-3"
              style={{
                background: unlocked ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${unlocked ? 'rgba(249,115,22,0.45)' : 'rgba(255,255,255,0.08)'}`,
              }}
              data-testid={`achievement-item-${a.id}`}>
              <div className="flex items-center gap-3">
                <span className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background: unlocked ? 'linear-gradient(180deg, #F97316, #C2410C)' : 'rgba(255,255,255,0.06)',
                    boxShadow: unlocked ? '0 4px 14px rgba(249,115,22,0.55)' : 'none',
                  }}>
                  <Trophy className="w-5 h-5" style={{ color: unlocked ? '#fff' : 'rgba(255,255,255,0.5)' }} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="display font-bold text-sm" style={{ color: '#fff' }}>{a.title || a.name}</p>
                  {a.description && <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.65)' }}>{a.description}</p>}
                </div>
                {a.reward && (
                  <span className="shrink-0 display font-extrabold text-xs px-2 py-0.5 rounded"
                    style={{ background: unlocked ? '#FFD93D' : 'rgba(255,255,255,0.08)', color: unlocked ? '#3F2A00' : 'rgba(255,255,255,0.6)' }}>
                    {a.reward}
                  </span>
                )}
              </div>
              <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{
                  width: `${progress}%`,
                  background: unlocked ? 'linear-gradient(90deg, #F97316, #FFD93D)' : 'linear-gradient(90deg, var(--vl-btn-primary-from, #004D61), var(--vl-btn-primary-to, #004D61))',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <p className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {progress}% {unlocked ? '· unlocked' : ''}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
